import {
  pgTable,
  text,
  doublePrecision,
  timestamp,
  bigint,
  uuid,
  boolean,
  integer,
  primaryKey,
  index,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

import {
  banks,
  plans,
  userWorkAreaOptions,
  userHowToKnowOptions,
} from "@/constants";

const bank = text("banco", {
  enum: [banks.caixa.value, banks.santander.value, banks.bradesco.value],
});

const workArea = text("area_de_atuacao", {
  enum: userWorkAreaOptions,
});

const howToKnow = text("como_conheceu", {
  enum: userHowToKnowOptions,
});

export const usuarios = pgTable("usuarios", {
  id: text("id").primaryKey().notNull(), // Clerk user ID
  provedor_id: text("provedor_id").unique(), // Stripe user ID
  email: text("email").notNull().unique(),
  nome: text("nome"),
  telefone: text("telefone"),
  uf: text("uf"),
  area_de_atuacao: workArea,
  como_conheceu: howToKnow,
  plano: text("plano", {
    enum: [plans.gratis.value, plans.especialista.value],
  })
    .notNull()
    .default(plans.gratis.value),
  criado_em: timestamp("criado_em").notNull().defaultNow(),
  atualizado_em: timestamp("atualizado_em"),
});

export const leiloes = pgTable(
  "leiloes",
  {
    id: uuid("id").defaultRandom().primaryKey().notNull(),
    numero_do_leilao: text("numero_do_leilao").unique(),
    banco: bank.notNull().default(banks.caixa.value),
    uf: text("uf"),
    cidade: text("cidade"),
    cidade_descricao: text("cidade_descricao"),
    bairro: text("bairro"),
    endereco: text("endereco"),
    preco1: doublePrecision("preco1"),
    preco2: doublePrecision("preco2"),
    desconto: doublePrecision("desconto"),
    valor_de_avaliacao: doublePrecision("valor_de_avaliacao"),
    descricao: text("descricao"),
    modalidade_de_venda: text("modalidade_de_venda"),
    link: text("link"),
    tipo: text("tipo"),
    total_area: doublePrecision("total_area"),
    total_area_privativa: doublePrecision("total_area_privativa"),
    total_area_terreno: doublePrecision("total_area_terreno"),
    data_de_licitacao1: bigint("data_de_licitacao1", { mode: "number" }),
    data_de_licitacao2: bigint("data_de_licitacao2", { mode: "number" }),
    inscricao_imobiliaria: text("inscricao_imobiliaria"),
    comarca: text("comarca"),
    averbacao: text("averbacao"),
    matricula: text("matricula"),
    imagens: text("imagens"),
    edital: text("edital"),
    situacao: text("situacao"),
    numero_do_item_edital: text("numero_do_item_edital"),
    aceita_fgts: boolean("aceita_fgts"),
    aceita_financiamento: boolean("aceita_financiamento"),
    aceita_parcelamento: boolean("aceita_parcelamento"),
    aceita_consorcio: boolean("aceita_consorcio"),
    aceita_a_vista: boolean("aceita_a_vista"),
    area_nao_averbada: boolean("area_nao_averbada"),
    acoes_judiciais: text("acoes_judiciais"),
    criado_em: timestamp("criado_em").notNull().defaultNow(),
    atualizado_em: timestamp("atualizado_em"),
    excluido: boolean("excluido"),
    excluido_em: timestamp("excluido_em"),
  },
  (table) => [
    index("search_index").using(
      "gin",
      sql`(
        setweight(to_tsvector('portuguese', ${table.tipo}), 'A') ||
        setweight(to_tsvector('portuguese', ${table.cidade_descricao}), 'A') ||
        setweight(to_tsvector('portuguese', ${table.endereco}), 'B') ||
        setweight(to_tsvector('portuguese', ${table.bairro}), 'C') ||
        setweight(to_tsvector('portuguese', ${table.descricao}), 'D')
    )`
    ),
    index("idx_leiloes_banco").on(table.banco),
    index("idx_leiloes_excluido").on(table.excluido),
    index("idx_leiloes_comarca").on(table.comarca),
    index("idx_leiloes_uf").on(table.uf),
    index("idx_leiloes_cidade_descricao").on(table.cidade_descricao),
    index("idx_leiloes_bairro").on(table.bairro),
    index("idx_leiloes_tipo").on(table.tipo),
    index("idx_leiloes_modalidade_de_venda").on(table.modalidade_de_venda),
    // composite index (order matters)
    index("idx_leiloes_uf_cidade_descricao").on(
      table.uf,
      table.cidade_descricao
    ),
    index("idx_leiloes_uf_excluido_comarca").on(
      table.uf,
      table.excluido,
      table.comarca
    ),
    index("idx_leiloes_uf_cidade_descricao_tipo").on(
      table.uf,
      table.cidade_descricao,
      table.tipo
    ),
    index("idx_leiloes_uf_banco").on(table.uf, table.banco),
    index("idx_leiloes_uf_banco_cidade_descricao").on(
      table.uf,
      table.banco,
      table.cidade_descricao
    ),
    index("idx_leiloes_uf_banco_cidade_descricao_tipo").on(
      table.uf,
      table.banco,
      table.cidade_descricao,
      table.tipo
    ),
    index("idx_leiloes_datas").on(
      table.data_de_licitacao1,
      table.data_de_licitacao2
    ),
    index("idx_leiloes_preco").on(table.preco1, table.preco2),
  ]
);

export const totalPropriedades = pgTable("total_propriedades", {
  id: uuid("id").defaultRandom().primaryKey().notNull(),
  uf: text("uf").unique(),
  total: integer("total"),
  ultima_atualizacao_caixa: timestamp("ultima_atualizacao_caixa"),
  ultima_atualizacao_santander: timestamp("ultima_atualizacao_santander"),
  ultima_atualizacao_bradesco: timestamp("ultima_atualizacao_bradesco"),
});

export const favoritos = pgTable(
  "favoritos",
  {
    usuario_id: text("usuario_id")
      .notNull()
      .references(() => usuarios.id),
    leilao_id: uuid("leilao_id")
      .notNull()
      .references(() => leiloes.id),
    criado_em: timestamp("criado_em").notNull().defaultNow(),
  },
  (table) => [
    primaryKey({
      columns: [table.usuario_id, table.leilao_id],
    }),
  ]
);

export const alertas = pgTable("alertas", {
  id: uuid("id").defaultRandom().primaryKey().notNull(),
  usuario_id: text("usuario_id")
    .notNull()
    .references(() => usuarios.id),
  titulo: text("titulo").notNull(),
  email: text("email").notNull(),
  telefone: text("telefone"),
  uf: text("uf").notNull(),
  cidades: text("cidades"),
  endereco: text("endereco"),
  bairros: text("bairros"),
  area: doublePrecision("area"),
  dias_faltantes_leilao: doublePrecision("dias_faltantes_leilao"),
  tipo: text("tipo"),
  modalidade_de_venda: text("modalidade_de_venda"),
  pagamento: text("pagamento"),
  preco_min: doublePrecision("preco_min"),
  preco_max: doublePrecision("preco_max"),
  desconto: doublePrecision("desconto"),
  criado_em: timestamp("criado_em").notNull().defaultNow(),
});

export const matriculas = pgTable("matriculas", {
  id: uuid("id").defaultRandom().primaryKey().notNull(),
  usuario_id: text("usuario_id")
    .notNull()
    .references(() => usuarios.id),
  titulo: text("titulo").notNull(),
  uf: text("uf"),
  numero_do_leilao: text("numero_do_leilao").unique(),
  banco: bank,
  inscricao_imobiliaria: text("inscricao_imobiliaria"),
  matricula: text("matricula"),
  criado_em: timestamp("criado_em").notNull().defaultNow(),
});

export const analises = pgTable(
  "analises",
  {
    usuario_id: text("usuario_id")
      .notNull()
      .references(() => usuarios.id),
    matricula_id: uuid("matricula_id")
      .notNull()
      .references(() => matriculas.id),
    criado_em: timestamp("criado_em").notNull().defaultNow(),
  },
  (table) => [
    primaryKey({
      columns: [table.usuario_id, table.matricula_id],
    }),
  ]
);

export const onboarding = pgTable(
  "onboarding",
  {
    usuario_id: text("usuario_id")
      .notNull()
      .references(() => usuarios.id),
    favoritos: boolean("favoritos").default(false),
    alertas: boolean("alertas").default(false),
    viabilidades: boolean("viabilidades").default(false),
    analises: boolean("analises").default(false),
  },
  (table) => [
    primaryKey({
      columns: [table.usuario_id],
    }),
  ]
);

export const artigos = pgTable("artigos", {
  id: uuid("id").defaultRandom().primaryKey().notNull(),
  video_id: text("video_id").unique().notNull(),
  slug: text("slug"),
  publicado: boolean("publicado").default(false),
  raw: boolean("raw").default(false),
  criado_em: timestamp("criado_em").notNull().defaultNow(),
});

export const viabilidades = pgTable("viabilidades", {
  id: uuid("id").defaultRandom().primaryKey().notNull(),
  usuario_id: text("usuario_id")
    .notNull()
    .references(() => usuarios.id),
  leilao_id: uuid("leilao_id").references(() => leiloes.id),
  titulo: text("titulo").notNull(),
  // General
  uf: text("uf"),
  valor_arrematacao: doublePrecision("valor_arrematacao"),
  valor_avaliacao: doublePrecision("valor_avaliacao"),
  porcentagem_entrada: doublePrecision("porcentagem_entrada"),
  numero_de_parcelas_entrada: integer("numero_de_parcelas_entrada"),
  juros_anual: doublePrecision("juros_anual"),
  assesoria_inicial: doublePrecision("assesoria_inicial"),
  valor_outras_despesas_inicial: doublePrecision(
    "valor_outras_despesas_inicial"
  ),
  // Regularization
  comissao_leiloeiro: doublePrecision("comissao_leiloeiro"),
  comissao_leiloeiro_porcentagem: doublePrecision(
    "comissao_leiloeiro_porcentagem"
  ),
  taxa_itbi: doublePrecision("taxa_itbi"),
  taxa_itbi_porcentagem: doublePrecision("taxa_itbi_porcentagem"),
  taxa_escritura: doublePrecision("taxa_escritura"),
  taxa_escritura_porcentagem: doublePrecision("taxa_escritura_porcentagem"),
  taxa_registro: doublePrecision("taxa_registro"),
  taxa_registro_porcentagem: doublePrecision("taxa_registro_porcentagem"),
  taxa_frj: doublePrecision("taxa_frj"),
  valor_desocupacao: doublePrecision("valor_desocupacao"),
  dividas_propter_rem: doublePrecision("dividas_propter_rem"),
  valor_outras_despesas_regularizacao: doublePrecision(
    "valor_outras_despesas_regularizacao"
  ),
  // Sale
  valor_reforma: doublePrecision("valor_reforma"),
  comissao_corretor: doublePrecision("comissao_corretor"),
  comissao_corretor_porcentagem: doublePrecision(
    "comissao_corretor_porcentagem"
  ),
  assesoria_venda: doublePrecision("assesoria_venda"),
  assesoria_venda_porcentagem: doublePrecision("assesoria_venda_porcentagem"),
  trafego_pago: doublePrecision("trafego_pago"),
  taxa_irpf: doublePrecision("taxa_irpf"),
  valor_outras_despesas_venda: doublePrecision("valor_outras_despesas_venda"),
  // Monthly
  frequencia_iptu: text("frequencia_iptu"),
  valor_iptu: doublePrecision("valor_iptu"),
  valor_condominio: doublePrecision("valor_condominio"),
  // Result
  prazo_de_venda_meses: integer("prazo_de_venda_meses"),
  // Timestamps
  criado_em: timestamp("criado_em").notNull().defaultNow(),
});
